import React from "react";
import { Dialog, IconButton } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { Alert, Button, CircularProgress } from "@mui/material";
import usePixModalStyles from "./PixPaymentModal.styles";
import { PixPaymentModalProps, PixModalPaymentStatus } from "./types";
import PixHeader from "./sections/PixHeader";
import PixOrderInfo from "./sections/PixOrderInfo";
import PixQrCodeSection from "./sections/PixQrCodeSection";
import PixValueSection from "./sections/PixValueSection";
import PixConfirmation from "./sections/PixConfirmation";
import PixPaymentDetails from "./sections/PixPaymentDetails";
import PixFooter from "./sections/PixFooter";
import PixSummaryHeader from "./sections/PixSummaryHeader";
import PixTabs from "./sections/PixTabs";
import { isPixPaid } from "@pages/User/MyOrders/utilsOrders/utilsMyOrders";

const PixPaymentModal: React.FC<PixPaymentModalProps> = ({
  open,
  onClose,
  orderNumber,
  orderStatus,
  customerName,
  productCode,
  productName,
  volume,
  deliveryDate,
  payloads,
  activeIndex,
  onSelectTab,
  aggregates,
  orderTotal,
  onRefreshQrCode,
  loading = false,
  refreshing = false,
  error = null,
}) => {
  const classes = usePixModalStyles();

  const activePix = payloads[activeIndex] ?? payloads[0] ?? null;
  const hasMultiplePix = payloads.length > 1;
  const isComplement = activePix ? activePix.main === false : false;

  // Status do pagamento do Pix da aba ativa (regra confiável — D4).
  const paymentStatus: PixModalPaymentStatus =
    activePix && isPixPaid(activePix) ? "confirmed" : "pending";
  const isPending = paymentStatus === "pending";

  // Valor da aba ativa; fallback para o total da ordem quando não há Pix ainda.
  const amount = activePix?.valor ?? orderTotal ?? aggregates.total;
  const copiaECola = activePix?.copiaECola ?? "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      PaperProps={{
        className: classes.dialogPaper,
      }}
    >
      {loading ? (
        <div className={classes.loadingContainer}>
          <IconButton
            size="small"
            className={classes.loadingCloseButton}
            onClick={onClose}
            aria-label="Fechar modal"
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </IconButton>
          <CircularProgress color="primary" size={48} />
          <span className={classes.loadingText}>
            Carregando dados do pedido...
          </span>
        </div>
      ) : (
        <>
          <PixHeader paymentStatus={paymentStatus} onClose={onClose} />
          <div className={classes.body}>
            {error && (
              <Alert
                severity="error"
                action={
                  onRefreshQrCode && (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={onRefreshQrCode}
                      disabled={refreshing}
                    >
                      Tentar novamente
                    </Button>
                  )
                }
              >
                {error}
              </Alert>
            )}

            <PixOrderInfo
              orderNumber={orderNumber}
              orderStatus={orderStatus}
              customerName={customerName}
              productCode={productCode}
              productName={productName}
              volume={volume}
              deliveryDate={deliveryDate}
              paymentStatus={paymentStatus}
            />

            {/* Cabeçalho consolidado (total + "X de Y pagos") — exibido quando
                há mais de um Pix na ordem (D5/D6). */}
            {hasMultiplePix && (
              <PixSummaryHeader
                total={aggregates.total}
                paidCount={aggregates.paidCount}
                count={aggregates.count}
              />
            )}

            {/* Abas (Pix 1, Pix 2, …) — apenas quando há mais de um Pix (D6). */}
            {hasMultiplePix && (
              <PixTabs
                payloads={payloads}
                activeIndex={activeIndex}
                onSelect={onSelectTab}
              />
            )}

            {isPending ? (
              <>
                <div
                  className={`${classes.qrSection} ${
                    copiaECola ? "" : classes.qrSectionSingle
                  }`}
                >
                  <PixQrCodeSection qrCodeValue={copiaECola} />
                  {/* Quando não há copia e cola, só exibimos a mensagem de QR
                      Code indisponível — os campos ao lado e o texto de ajuda
                      abaixo ficam ocultos. */}
                  {copiaECola && (
                    <PixValueSection
                      amount={amount}
                      pixCopiaECola={copiaECola}
                      isComplement={isComplement}
                    />
                  )}
                </div>
                {copiaECola && (
                  <p className={classes.qrHelperText}>
                    Pague somente por este QR Code. Ele é atualizado se o valor
                    do pedido mudar, e o pedido é liberado após a confirmação do
                    banco.
                  </p>
                )}
              </>
            ) : (
              <PixConfirmation amount={amount} />
            )}

            <PixPaymentDetails
              receiver={activePix?.receiverName}
              bank={activePix?.bankName}
              transactionId={activePix?.transactionId}
              issueDate={activePix?.issueDate}
              paymentStatus={paymentStatus}
            />
          </div>

          <PixFooter
            paymentStatus={paymentStatus}
            onClose={onClose}
            // Refresh só é permitido no Pix principal: o endpoint de refresh do
            // SAP não aceita UUID, então em complementares não há como garantir
            // qual Pix seria regenerado. Desabilita até o backend suportar UUID.
            onRefreshQrCode={
              isPending && !isComplement ? onRefreshQrCode : undefined
            }
            refreshing={refreshing}
          />
        </>
      )}
    </Dialog>
  );
};

export default PixPaymentModal;
